import React, { Fragment } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { DotsVerticalIcon } from '@heroicons/react/solid';
import {
  DownloadIcon,
  DuplicateIcon,
  PencilIcon,
  PencilAltIcon,
  TrashIcon,
} from '@heroicons/react/outline';
import useLocale from 'next-translate/useTranslation';
import { useWorkflowStore } from '@/lib/workflows/store';

interface NodeActionsDropdownProps {
  nodeId: string;
  nodeTitle?: string;
  mediaUrl?: string;
  mediaType: 'image' | 'video' | 'audio';
  filename?: string;
  readOnly?: boolean;
  onEditImage?: () => void;
}

const downloadMedia = async (url: string, filename: string) => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error('Download failed:', error);
  }
};

const getDefaultFilename = (
  mediaType: 'image' | 'video' | 'audio',
  url?: string,
): string => {
  const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '');
  const extensions: Record<string, string> = {
    image: 'png',
    video: 'mp4',
    audio: 'mp3',
  };

  // Try to extract extension from URL
  if (url) {
    const match = url.match(/\.(\w+)(?:\?|$)/);
    if (match) {
      return `download-${timestamp}.${match[1]}`;
    }
  }

  return `download-${timestamp}.${extensions[mediaType]}`;
};

export function NodeActionsDropdown({
  nodeId,
  nodeTitle,
  mediaUrl,
  mediaType,
  filename,
  readOnly = false,
  onEditImage,
}: NodeActionsDropdownProps) {
  const { t } = useLocale('common');
  const resolvedNodeTitle = nodeTitle || t('this node');
  const deleteNode = useWorkflowStore((state) => state.deleteNode);
  const duplicateNode = useWorkflowStore((state) => state.duplicateNode);
  const updateNodeData = useWorkflowStore((state) => state.updateNodeData);

  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!mediaUrl) return;
    const downloadFilename =
      filename || getDefaultFilename(mediaType, mediaUrl);
    downloadMedia(mediaUrl, downloadFilename);
  };

  const handleDuplicate = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (readOnly) return;
    duplicateNode(nodeId);
  };

  const handleRename = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (readOnly) return;
    const newName = prompt(t('Enter new name:'), resolvedNodeTitle);
    if (newName && newName.trim() && newName !== resolvedNodeTitle) {
      updateNodeData(nodeId, { label: newName.trim() });
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (readOnly) return;
    if (confirm(t('Delete "{{title}}" node?', { title: resolvedNodeTitle }))) {
      deleteNode(nodeId);
    }
  };

  return (
    <Menu as='div' className='relative'>
      <Menu.Button
        className='flex-shrink-0 p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors'
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        <span className='sr-only'>{t('Open options')}</span>
        <DotsVerticalIcon
          className='h-5 w-5 text-gray-600 dark:text-gray-300'
          aria-hidden='true'
        />
      </Menu.Button>
      <Transition
        as={Fragment}
        enter='transition ease-out duration-100'
        enterFrom='transform opacity-0 scale-95'
        enterTo='transform opacity-100 scale-100'
        leave='transition ease-in duration-75'
        leaveFrom='transform opacity-100 scale-100'
        leaveTo='transform opacity-0 scale-95'
      >
        <Menu.Items className='absolute right-0 z-50 mt-2 w-40 origin-top-right rounded-md bg-white dark:bg-gray-800 py-1 shadow-lg ring-1 ring-black ring-opacity-5 dark:ring-gray-700 focus:outline-none'>
          {mediaUrl && (
            <Menu.Item>
              {({ active }) => (
                <button
                  onClick={handleDownload}
                  className={`${
                    active ? 'bg-gray-100 dark:bg-gray-700' : ''
                  } flex w-full items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-200`}
                >
                  <DownloadIcon className='mr-3 h-4 w-4 text-gray-400 dark:text-gray-500' />
                  {t('Download')}
                </button>
              )}
            </Menu.Item>
          )}
          {onEditImage && mediaUrl && mediaType === 'image' && (
            <Menu.Item>
              {({ active }) => (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditImage();
                  }}
                  className={`${
                    active ? 'bg-gray-100 dark:bg-gray-700' : ''
                  } flex w-full items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-200`}
                >
                  <PencilAltIcon className='mr-3 h-4 w-4 text-gray-400 dark:text-gray-500' />
                  {t('Edit Image')}
                </button>
              )}
            </Menu.Item>
          )}
          {!readOnly && (
            <>
              <Menu.Item>
                {({ active }) => (
                  <button
                    onClick={handleRename}
                    className={`${
                      active ? 'bg-gray-100 dark:bg-gray-700' : ''
                    } flex w-full items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-200`}
                  >
                    <PencilIcon className='mr-3 h-4 w-4 text-gray-400 dark:text-gray-500' />
                    {t('Rename')}
                  </button>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <button
                    onClick={handleDuplicate}
                    className={`${
                      active ? 'bg-gray-100 dark:bg-gray-700' : ''
                    } flex w-full items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-200`}
                  >
                    <DuplicateIcon className='mr-3 h-4 w-4 text-gray-400 dark:text-gray-500' />
                    {t('Duplicate')}
                  </button>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <button
                    onClick={handleDelete}
                    className={`${
                      active ? 'bg-red-50 dark:bg-red-900/20' : ''
                    } flex w-full items-center px-4 py-2 text-sm text-red-600 dark:text-red-400`}
                  >
                    <TrashIcon className='mr-3 h-4 w-4 text-red-500 dark:text-red-400' />
                    {t('Delete')}
                  </button>
                )}
              </Menu.Item>
            </>
          )}
        </Menu.Items>
      </Transition>
    </Menu>
  );
}
